import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Grid from '../components/Grid'; // Assuming Grid is a reusable component for displaying data
import { FaEye, FaTimes, FaDownload } from "react-icons/fa";
import { CgEnter } from "react-icons/cg";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

import { DataGrid } from '@mui/x-data-grid';
import { create } from '@mui/material/styles/createTransitions';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Payments({ onAddPaymentClick }) {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPyament, setSelectedPayment] = useState(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          toast.error('Authentication token is missing. Please log in again.');
          return;
        }
  
        const response = await axios.get(`${API_BASE_URL}/paymentReceipts/get`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        // Sort by createdAt before setting state
        const sortedPayments = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  
        setPayments(sortedPayments);
  
      } catch (error) {
        console.error('Error fetching payments:', error);
        toast.error('Error fetching data');
      }
    };
  
    fetchPayments();
  }, []);
  


  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredPayments : payments;

  if (dataToExport.length === 0) {
    toast.warn("No payment data to export.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Payment Receipts");

  const headerRow = [
    "Payment Receipt ID",
    "Customer Code",
    "Customer Name",
    "Date",
    "Amount (LKR)",
    "Status",
    "Created By"
  ];
  worksheet.addRow(headerRow);

  const header = worksheet.getRow(1);
  header.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD966" }, // Light yellow
    };
    cell.font = { bold: true };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  dataToExport.forEach((payment) => {
    worksheet.addRow([
      payment.payment_receipt_id,
      payment.customer_id?.customer_code || "N/A",
      payment.customer_id?.customer_name || "N/A",
      new Date(payment.createdAt).toLocaleDateString(),
      Number(payment.amount).toFixed(2),
      payment.status,
      payment.createdBy?.username || "Unknown"
    ]);
  });

  worksheet.columns.forEach((column) => {
    column.width = column.header
      ? column.header.toString().length < 20
        ? 20
        : column.header.toString().length
      : 20;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "payment_receipts.xlsx");
};

const handleCancelPayment = async (payment_receipt_id) => {
  if (!window.confirm("Are you sure you want to cancel this payment?")) return;

  try {
    const token = sessionStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token is missing. Please log in again.");
      return;
    }

    // Backend cancel endpoint (you can adjust the route name)
    const response = await axios.put(
      `${API_BASE_URL}/paymentReceipts/cancel/${payment_receipt_id}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status === 200) {
      toast.success("Payment cancelled successfully!");

      // Re-fetch payment list
      const updatedList = payments.map((p) =>
        p.payment_receipt_id === payment_receipt_id
          ? { ...p, status: "Cancelled" }
          : p
      );
      setPayments(updatedList);
    }
  } catch (error) {
    console.error("Error cancelling payment:", error);
    toast.error(error.response?.data?.message || "Failed to cancel payment");
  }
};

  const fetchPaymentDetails = async (payment_receipt_id) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token is missing. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/paymentReceipts/get/${payment_receipt_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });


      setSelectedPayment(response.data); 

    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to fetch payment details');
    }
  };

  const [sort] = useState({
    field: 'date',
    direction: 'asc',
  });
  const [selectedSearchField, setSelectedSearchField] = useState('payment_receipt_id');

  const filteredPayments = payments
    .filter((payment) => {
      if (selectedSearchField === 'payment_receipt_id') {
        return payment.payment_receipt_id.toLowerCase().includes(searchTerm.toLowerCase());
      }
      if (selectedSearchField === 'amount') {
        return payment.amount.toString().includes(searchTerm);
      }
      if (selectedSearchField === 'date') {
        return new Date(payment.createdAt).toLocaleDateString().includes(searchTerm);
      }
      if (selectedSearchField === 'status') {
        return payment.status.toLowerCase().includes(searchTerm.toLowerCase());
      }
      if (selectedSearchField === 'customer_id') {
        return payment.customer_id?.customer_code.toLowerCase().includes(searchTerm.toLowerCase());
      }
      if (selectedSearchField === 'createdBy') {
        return payment.createdBy?.username.toLowerCase().includes(searchTerm.toLowerCase());
      }
      if (selectedSearchField === 'customer_name') {
        return payment.customer_id?.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true; // Default case if no field matches
    })
    .sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      if (sort.direction === 'desc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
    
    const columns = [
      { field: 'payment_receipt_id', headerName: 'Payment Receipt ID', flex: 1 },
      {
        field: "createdAt",
        headerName: "Created Date",
        flex: 1,
        renderCell: (params) => (
          <span>
            {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
          </span>
        ),
      },
      { 
        field: 'customer_code', 
        headerName: 'Customer Code', 
        flex: 1,
        renderCell: (params) => {
          // Accessing customer_code directly from the customer_id object
          return <span>{params.row.customer_id?.customer_code || "No Customer Code"}</span>
        }
      },
      { 
        field: 'customer_name', 
        headerName: 'Customer Name', 
        flex: 1,
        renderCell: (params) => {
          // Accessing customer_name directly from the customer_id object
          return <span>{params.row.customer_id?.customer_name || "No Customer Name"}</span>
        }
      },
      { field: 'amount', headerName: 'Amount (LKR)', flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
      { 
        field: "status",
        headerName: "Status",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
            const status = params.row.status;
        
            // Map statuses to classes
            const statusClassMap = {
              "Unutilized": "bg-[#FBC02D] text-white",
              "Partially Utilized": "bg-[#8BC34A] text-white",
              "Fully Utilized": "bg-[#90A4AE] text-white",
            };
      
          // Get the class based on status or default to a neutral color
          const statusClass = statusClassMap[status] || "bg-gray-500 text-white"; // Default class if status not found
      
          return (
            <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
              {status}
            </span>
          );
        }, 
      },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return <span>{params.row.createdBy?.username || "Unknown"}</span>;
        },
      },
      {
        field: "action",
        headerName: "Action",
        flex: 1,
        renderCell: (params) => {
          const isSettled =
            params.row.status === "Partially Utilized" || params.row.status === "Canceled" ||
            params.row.status === "Fully Utilized";

          return (
            <div className="flex items-center gap-2">
              {/* View Details Button */}
              <button
                className="bg-[#FBC02D] text-white p-2 rounded"
                onClick={() => fetchPaymentDetails(params.row.payment_receipt_id)}
                title="View Payment Details"
              >
                <FaEye />
              </button>

              {/* Cancel Payment Button */}
              <button
                onClick={() => handleCancelPayment(params.row.payment_receipt_id)}
                className={`text-white p-2 flex items-center justify-center rounded ${
                  isSettled ? "bg-gray-400 cursor-not-allowed" : "bg-[#d82828] hover:bg-red-700"
                }`}
                title={isSettled ? "Cannot cancel settled payment" : "Cancel Payment"}
                disabled={isSettled}
              >
                <FaTimes />
              </button>
            </div>
          );
        },
      },
    ];
    

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Receipts</h1>
          <button
            onClick={onAddPaymentClick}
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Add Payment
          </button>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="payment_receipt_id">Payment Receipt ID</option>
                <option value="amount">Amount</option>
                <option value="date">Date</option>
                <option value="status">Status</option>
                <option value="customer_id">Customer Code</option>
                <option value="createdBy">Created By</option>
                <option value="customer_name">Customer Name</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Payments"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>
          <Grid rows={filteredPayments} columns={columns} getRowId={(row) => row.payment_receipt_id}/>
          {selectedPyament && (
            <div
              className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
              style={{ overflowY: "auto" }}
            >
              <div
                className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto"
                style={{ maxHeight: "80vh", overflowY: "auto",  }}
              >
                <button
                  onClick={() => setSelectedPayment(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
                >
                  &times;
                </button>
                <h2 className="text-3xl font-bold mb-4">Payment Receipt Details</h2>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Payment Receipt ID</label>
                  <input
                    type="text"
                    value={selectedPyament.payment_receipt_id || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Customer Code</label>
                  <input
                    type="text"
                    value={selectedPyament.customer_id.customer_code || "Unkown"}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Customer Name</label>
                  <input
                    type="text"
                    value={selectedPyament.customer_id.customer_name || "Unkown"}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Amount</label>
                  <input
                    type="text"
                    value={selectedPyament.amount || 0}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Date</label>
                  <input
                    type="text"
                    value={selectedPyament.payment_date || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                {/* Display payment receipts */}
                <h3 className="text-lg font-semibold mt-4 mb-2">Invoices</h3>
                <div  style={{ height: 400, width: "100%" }}>
                  <DataGrid
                  rows={selectedPyament.invoices.map((invoice, index) => ({
                    id: index + 1,
                    invoiceID: invoice.invoice_id.invoice_id,
                    amount_applied: invoice.amount_applied,
                  }))}
                  columns={[
                    { field: "id", headerName: "Number", width: 80, flex: 0.3 },
                    { field: "invoiceID", headerName: "Invoice  ID", width: 150, flex: 1 },
                    { field: "amount_applied", headerName: "Applied Amount (LKR)", width: 140, flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
                  ]}
                  pageSize={16}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Payments;
